import { FC, memo } from 'react'
import { NodeProps, Position } from '@xyflow/react'
import { useTranslate } from 'react-admin'
import Typography from '@mui/material/Typography'
import StorageIcon from '@mui/icons-material/Storage'
import BaseNode from './BaseNode'
import { DataStoreNodeData, DataStoreOperation } from '../types'
import { xxllncColor } from '../../../layout/theme/color'

const OPERATION_LABEL_KEY: Record<DataStoreOperation, string> = {
  write: 'workflow.node.dataStoreWriteVectorSummary',
  read: 'workflow.node.dataStoreReadVectorSummary',
  update: 'workflow.node.dataStoreUpdateSummary',
  delete: 'workflow.node.dataStoreDeleteSummary',
}

const PLAIN_LABEL_KEY: Partial<Record<DataStoreOperation, string>> = {
  write: 'workflow.node.dataStoreWritePlainSummary',
  read: 'workflow.node.dataStoreReadPlainSummary',
  delete: 'workflow.node.dataStoreDeletePlainSummary',
}

const DataStoreNode: FC<NodeProps> = ({ id, data, selected }) => {
  const translate = useTranslate()
  const nodeData = data as DataStoreNodeData
  const operation = nodeData.operation ?? 'read'
  const identifier = (nodeData.identifier ?? '').trim()
  const recordId = (nodeData.recordId ?? '').trim()
  const hasContent = Boolean((nodeData.content ?? '').trim())
  const hasQuery = Boolean((nodeData.query ?? '').trim())
  const isPlain = nodeData.storageMode === 'plain'
  const keyValue = (nodeData.key ?? '').trim()

  // eslint-disable-next-line security/detect-object-injection
  const plainLabelKey = isPlain ? PLAIN_LABEL_KEY[operation] : undefined
  // eslint-disable-next-line security/detect-object-injection
  const opLabel = translate(plainLabelKey ?? OPERATION_LABEL_KEY[operation])
  const subtitle = (() => {
    if (operation === 'update' || (operation === 'delete' && !isPlain)) {
      return recordId
        ? `${opLabel} · ${recordId.slice(0, 8)}…`
        : translate('workflow.node.dataStoreMissingRecordId')
    }
    if (!identifier) return translate('workflow.node.dataStoreMissingIdentifier')
    if (operation === 'write' || operation === 'delete') {
      return isPlain && keyValue ? `${opLabel} · ${identifier} · ${keyValue}` : `${opLabel} · ${identifier}`
    }
    // read
    const detail = isPlain
      ? keyValue || translate('workflow.node.dataStoreReadAll')
      : `top ${nodeData.topK ?? 5}`
    return `${opLabel} · ${identifier} · ${detail}`
  })()

  const showError = (() => {
    if (operation === 'delete') return isPlain ? !identifier || !keyValue : !recordId
    if (operation === 'update') return !recordId || !hasContent
    if (operation === 'write') return !identifier || !hasContent || (isPlain && !keyValue)
    if (operation === 'read') return !identifier || (!isPlain && !hasQuery)
    return false
  })()
  const errorText = (() => {
    if (operation === 'delete' && !isPlain && !recordId)
      return translate('workflow.node.dataStoreMissingRecordId')
    if (operation === 'delete' && isPlain && identifier && !keyValue)
      return translate('workflow.node.dataStoreMissingKey')
    if (operation === 'update' && !recordId)
      return translate('workflow.node.dataStoreMissingRecordId')
    if (operation === 'update' && !hasContent)
      return translate('workflow.node.dataStoreMissingContent')
    if (!identifier) return translate('workflow.node.dataStoreMissingIdentifier')
    if (operation === 'write' && isPlain && !keyValue)
      return translate('workflow.node.dataStoreMissingKey')
    if (operation === 'write' && !hasContent)
      return translate('workflow.node.dataStoreMissingContent')
    if (operation === 'read' && !isPlain && !hasQuery)
      return translate('workflow.node.dataStoreMissingQuery')
    return ''
  })()

  return (
    <BaseNode
      // eslint-disable-next-line camelcase
      nodeType="data_store"
      icon={<StorageIcon fontSize="small" />}
      label={nodeData.label}
      nodeId={id}
      subtitle={subtitle}
      selected={selected}
      handles={[
        { type: 'target', position: Position.Top },
        { type: 'source', position: Position.Bottom },
      ]}
    >
      {showError && (
        <Typography
          variant="caption"
          sx={{
            color: xxllncColor.orange100,
            fontWeight: 600,
            display: 'block',
            lineHeight: 1.35,
          }}
        >
          {errorText}
        </Typography>
      )}
    </BaseNode>
  )
}

export default memo(DataStoreNode)
